/**
 * @file usePageSeoIssues.ts
 * @description SEO-предупреждения отмеченных страниц в окне «Опубликовать»
 * (phase19.16, партия Р3в, v0.19.64; SB127, SB132; контракт
 * r3v-api-contract §6 `GET /admin/site/pages/:id/seo-check`): H1, длина
 * заголовка и описания, фото без описания, битые ссылки — по ЧЕРНОВИКУ
 * страницы. Публикацию не блокируют. Читаются один раз на страницу и
 * отпечаток черновика (как подсказки стартового шаблона); сбой — без
 * предупреждений: это подсказка, не запрет.
 */

import { SITE_SEO_ISSUES } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { shopSeo } from "../../../lib/api-site-seo";
import type { PublishPageRow, RowSelection } from "../../../lib/site-builder/page-publish-items";

/** pageId → строки предупреждений черновика с этим отпечатком. */
type Found = Record<string, { fingerprint: string; lines: string[] }>;

export function usePageSeoIssues(rows: readonly PublishPageRow[], selection: Readonly<Record<string, RowSelection>>) {
  const [found, setFound] = useState<Found>({});
  const wanted = rows.filter((row) => selection[row.pageId]?.checked && !row.showOnly && (row.source.kind === "CUSTOM" || row.source.kind === "SYSTEM_HOME"));
  const missing = wanted.filter((row) => found[row.pageId]?.fingerprint !== row.fingerprint);
  const key = missing.map((row) => `${row.pageId}:${row.fingerprint}`).join("|");

  useEffect(() => {
    if (missing.length === 0) return;
    let alive = true;
    for (const row of missing) {
      shopSeo
        .pageCheck(row.pageId)
        // Заголовок замечания — из общего реестра shared; уникальные строки по порядку.
        .then((issues) => [...new Set(issues.map((i) => SITE_SEO_ISSUES[i.code]?.title ?? i.code))])
        .catch(() => [] as string[])
        .then((lines) => {
          if (alive) setFound((prev) => ({ ...prev, [row.pageId]: { fingerprint: row.fingerprint, lines } }));
        });
    }
    return () => {
      alive = false;
    };
    // Ключ — набор непроверенных страниц и их отпечатков.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /** Предупреждения страницы пункта (только у отмеченного, по актуальному отпечатку). */
  return (row: PublishPageRow): string[] => {
    const entry = found[row.pageId];
    return selection[row.pageId]?.checked && entry?.fingerprint === row.fingerprint ? entry.lines : [];
  };
}
